import { Card } from "@ui/card";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { Skeleton } from "@ui/skeleton";
import TranslatedText from "@components/items/display/TranslatedText";
import { ResourcesKey } from "@modules/localizations/translate";
import { SVG } from "@components/items/common/SVG";
import { Server } from "@wajeez/wajeez.microservices";
import { useEffect, useState } from "react";

export interface RamadanContentListParameters {
  quranUrl: string | null | undefined;
  programsUrl: string | null | undefined;
  prayersUrl: string | null | undefined;
  podcastsUrl: string | null | undefined;
  booksUrl: string | null | undefined;
}

interface RamadanContentListProps {
  parameterValues: Partial<RamadanContentListParameters>;
  content: Server.ContentEntityOutput | undefined | null;
  isLoading: boolean;
}

const RamadanContentList = ({
  parameterValues,
  isLoading,
  content,
}: RamadanContentListProps) => {
  const navigate = useAppNavigate();
  const [data, setData] = useState<
    {
      title: string;
      icon: string;
      param: string;
      link: string | null;
    }[]
  >();

  useEffect(() => {
    let timer1 = setTimeout(() => {
      const contentItems = Object.values(parameterValues)[0]
        ? Object.entries(parameterValues).map(([key, value]) => {
            const contentMapping: Record<
              string,
              { title: string; icon: string }
            > = {
              booksUrl: {
                title: "ramadan-books",
                icon: "/assets/icons/ramadan-books.svg",
              },
              programsUrl: {
                title: "ramadan-programs",
                icon: "/assets/icons/ramadan-programs.svg",
              },
              quranUrl: {
                title: "quran",
                icon: "/assets/icons/quran.svg",
              },
              prayersUrl: {
                title: "ramadan-supplications",
                icon: "/assets/icons/ramadan-supplications.svg",
              },
              podcastsUrl: {
                title: "ramadan-podcast",
                icon: "/assets/icons/ramadan-podcast.svg",
              },
            };
            const { title, icon } = contentMapping[key];
            return {
              param: key,
              link: value,
              title: title,
              icon: icon,
            };
          })
        : [];
      setData(contentItems);
    }, 50);

    return () => {
      clearTimeout(timer1);
    };
  }, [Object.values(parameterValues)[0]]);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-around pb-8 pt-5`}>
        {data?.map((item) => (
          <div
            key={item.param}
            className="flex flex-col items-center justify-center gap-3"
          >
            <Skeleton className="h-24 w-24 rounded-lg" />
            <Skeleton className="h-6 w-24 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-around pb-8 pt-5`}>
      {data?.map((item) => (
        <div
          key={item.param}
          className="group flex cursor-pointer flex-col items-center justify-center gap-3 hover:opacity-80"
          onClick={() => {
            if (item.link?.includes("/details")) {
              navigate("/" + item.link?.split("https://wajeez/")?.[1] || "");
            } else if (item.link?.includes("/page")) {
              navigate(
                `/cc?type=pg-ramadan&id=${
                  item.link?.split("https://wajeez/page/PG:RAMADAN/")?.[1] || ""
                }`
              );
            } else if (item.link?.includes("/component")) {
              navigate(
                `/c?type=com-vertical-list&id=${
                  item.link?.split(
                    "https://wajeez/component/COM:VERTICAL:LIST/"
                  )?.[1] || ""
                }`
              );
            }
          }}
        >
          <Card className="rounded-lg border-none bg-dark-ramadan-primary bg-opacity-50 p-5 shadow-md group-hover:shadow-lg dark:bg-opacity-100">
            <SVG src={item.icon} className="h-24 w-24" />
          </Card>
          <TranslatedText
            text={`ramadan.list-titles.${item.title}` as ResourcesKey}
            className="text-[20px] text-ramadan-primary dark:text-dark-ramadan-primary"
          />
        </div>
      ))}
    </div>
  );
};

export default RamadanContentList;
